import { getSubjectLabels } from '@/lib/settings';
import React, { useEffect, useMemo, useState } from 'react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import WizardShell from '@/components/ui/wizard-shell';
import CommunicationComposer, { type RecipientGroup, type GroupDraftState } from '@/components/communications/CommunicationComposer';
import { WizardCard, WizardSection, WizardKVGrid, WizardKVItem } from '@/components/ui/wizard-layout';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { formatDate } from '@/lib/dateFormat';
import { api, historyTemplatesApi, migrationApi, type MigrationCaseDto, type RenderHistoryTemplateResponse } from '@/lib/api';
import { cn } from '@/lib/utils';
import { getRoom, type Room } from '@/lib/rooms';
import { masterdataApi } from '@/lib/courses';
import { stundensatzAus } from '../../lib/honorar';

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  caseItem: MigrationCaseDto | null;
  onDone?: () => Promise<void> | void;
};

type StepState = 1 | 2 | 3;

type TeacherPersonPreview = {
  salutation?: string | null;
  name?: string | null;
  address?: string | null;
  structuredAddress?: {
    street?: string | null;
    postalCode?: string | null;
    city?: string | null;
  } | null;
  teacher?: {
    artRates?: Array<{
      unterrichtsform_art_id?: number | string;
      art_id?: number | string;
      rate?: number | string;
    }>;
  } | null;
  artRates?: Array<{
    unterrichtsform_art_id?: number | string;
    art_id?: number | string;
    rate?: number | string;
  }>;
};

type UnterrichtsformPreview = {
  id?: number;
  name?: string;
  art_id?: number | null;
  dauer?: number;
};

type PackagePreview = {
  id: string;
  name?: string;
  units?: number;
  totalCentsImmediate?: number;
  totalCents?: number;
  currency?: string;
};

function toMoney(cents?: number | null, currency = 'EUR'): string {
  if (typeof cents !== 'number') return '—';
  return `${(cents / 100).toFixed(2)} ${currency}`;
}

// ANG-1: Startdatum ohne Wochentag.
function formatDateOnly(date?: string | null): string {
  const value = String(date || '').trim();
  if (!value) return '—';
  const parsed = new Date(`${value}T00:00:00`);
  if (Number.isNaN(parsed.getTime())) return value;
  return new Intl.DateTimeFormat('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).format(parsed);
}

// ANG-1: Termin mit "wochentags" (z. B. donnerstags) statt "Donnerstag".
const WEEKDAY_TO_WOCHENTAGS: Record<string, string> = {
  Montag: 'montags',
  Dienstag: 'dienstags',
  Mittwoch: 'mittwochs',
  Donnerstag: 'donnerstags',
  Freitag: 'freitags',
  Samstag: 'samstags',
  Sonntag: 'sonntags',
};
function toWochentags(weekday?: string | null): string {
  const value = String(weekday || '').trim();
  if (!value) return '';
  return WEEKDAY_TO_WOCHENTAGS[value] ?? value.toLowerCase();
}

function splitStudentName(name?: string | null): { first: string; last: string } {
  const parts = String(name || '').trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return { first: '', last: '' };
  if (parts.length === 1) return { first: parts[0], last: '' };
  return { first: parts[0], last: parts.slice(1).join(' ') };
}

function channelPillClass(active: boolean): string {
  return cn(
    'inline-flex h-6 items-center rounded-full border px-2.5 text-xs font-medium',
    active
      ? 'border-primary bg-primary text-primary-foreground'
      : 'border-muted bg-muted/70 text-muted-foreground',
  );
}

const HISTORY_TEMPLATE_KEY = 'trial.offer.sent.history';

const HISTORY_DEFAULT = (teacherName: string, studentName: string, dateLabel: string): string =>
  `Angebot an Lehrer versendet: ${teacherName || 'Lehrer'} für ${studentName || 'Schüler'}${dateLabel ? ` (Startdatum ${dateLabel})` : ''}.`;

const MigrationOfferWizardModal: React.FC<Props> = ({ open, onOpenChange, caseItem, onDone }) => {
  const [step, setStep] = useState<StepState>(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);

  const [teacherPerson, setTeacherPerson] = useState<TeacherPersonPreview | null>(null);
  const [unterrichtsform, setUnterrichtsform] = useState<UnterrichtsformPreview | null>(null);
  const [selectedPackage, setSelectedPackage] = useState<PackagePreview | null>(null);
  const [offerAddress, setOfferAddress] = useState<string>('—');

  const [sendEmailTeacher, setSendEmailTeacher] = useState(true);
  const [sendSmsTeacher, setSendSmsTeacher] = useState(false);
  const [emailSubjectTeacher, setEmailSubjectTeacher] = useState('');
  const [emailHtmlTeacher, setEmailHtmlTeacher] = useState('');
  const [emailTextTeacher, setEmailTextTeacher] = useState('');

  const [historyHtml, setHistoryHtml] = useState('');

  const moveStep = (delta: 1 | -1) => {
    setStep((prev) => {
      const next = Math.min(3, Math.max(1, prev + delta)) as StepState;
      return next;
    });
  };

  // Aufbereitete Anzeige-Daten aus snapshot.office_contract_adjustment.payload.lesson
  const previewData = useMemo(() => {
    if (!caseItem) return null;
    const snapshot = (caseItem.snapshot ?? {}) as Record<string, unknown>;
    const office = (snapshot.office_contract_adjustment ?? {}) as {
      payload?: {
        lesson?: Record<string, unknown>;
        start_date?: string;
        legacy_updates?: { student?: Record<string, unknown> };
      };
    };
    const payload = office.payload ?? {};
    const lesson = (payload.lesson ?? {}) as Record<string, unknown>;
    const studentUpdate = (payload.legacy_updates?.student ?? {}) as Record<string, unknown>;
    const studentName = `${String(studentUpdate.first_name ?? '')} ${String(studentUpdate.last_name ?? '')}`.trim()
      || `${String(snapshot.legacy_student_first_name ?? '')} ${String(snapshot.legacy_student_last_name ?? '')}`.trim()
      || '—';
    const weekdayMap: Record<number, string> = { 1: 'Montag', 2: 'Dienstag', 3: 'Mittwoch', 4: 'Donnerstag', 5: 'Freitag', 6: 'Samstag', 7: 'Sonntag' };
    const weekdayNo = Number(lesson.weekday ?? 0);
    const startTime = String(lesson.start_time ?? '').slice(0, 5);
    const durationMin = Number(lesson.duration_min ?? 0);
    let endTime = '';
    if (startTime !== '' && durationMin > 0) {
      const [h, m] = startTime.split(':').map(Number);
      const baseMin = h * 60 + m + durationMin;
      endTime = `${String(Math.floor(baseMin / 60)).padStart(2, '0')}:${String(baseMin % 60).padStart(2, '0')}`;
    }
    const customer = (snapshot.customer_contract_adjustment ?? {}) as {
      decision?: { packageId?: string | null; followupPackageId?: string | null; paymentType?: string | null };
      finalization?: { order_id?: number | null; enrollment_id?: number | null };
    };
    const decision = customer.decision ?? {};
    const finalization = customer.finalization ?? {};
    return {
      studentName,
      teacherName: String(lesson.teacher_name ?? snapshot.legacy_teacher_name ?? '—'),
      teacherWpUserId: Number(lesson.teacher_wp_user_id ?? 0),
      instrument: String(lesson.fach_name ?? snapshot.legacy_fach_name ?? '—'),
      form: String(lesson.unterrichtsform_name ?? snapshot.legacy_uform_name ?? '—'),
      unterrichtsformId: Number(lesson.unterrichtsform_id ?? 0),
      weekday: weekdayMap[weekdayNo] ?? '',
      startTime,
      endTime,
      durationMin,
      roomName: String(lesson.room_name ?? snapshot.legacy_room_name ?? '—'),
      roomId: Number(lesson.room_id ?? 0),
      startDate: String(payload.start_date ?? snapshot.legacy_begin ?? ''),
      packageId: decision.packageId ? String(decision.packageId) : null,
      followupPackageId: decision.followupPackageId ? String(decision.followupPackageId) : null,
      orderId: Number(finalization.order_id ?? 0),
      enrollmentId: Number(finalization.enrollment_id ?? 0),
    };
  }, [caseItem]);

  const standortId = caseItem?.standort_id ?? 0;
  const teacherWpUserId = previewData?.teacherWpUserId ?? 0;

  // Honorar pro Termin aus teacher.artRates × durationMin (analog Trial-Wizard)
  const honorarPerLessonCents = useMemo(() => {
    if (!teacherPerson || !unterrichtsform?.art_id || !previewData?.durationMin) return null;
    const candidates = Array.isArray(teacherPerson.teacher?.artRates)
      ? teacherPerson.teacher?.artRates || []
      : (Array.isArray(teacherPerson.artRates) ? teacherPerson.artRates : []);
    const matched = candidates.find((entry) => {
      const art = Number(entry?.unterrichtsform_art_id ?? entry?.art_id ?? 0);
      return art === unterrichtsform.art_id;
    });
    if (!matched) return null;
    const ratePerHourEuro = Number(matched.rate ?? 0);
    if (!Number.isFinite(ratePerHourEuro) || ratePerHourEuro <= 0) return null;
    const ratePerHourCents = Math.round(ratePerHourEuro * 100);
    return Math.round(ratePerHourCents * (previewData.durationMin / 60));
  }, [teacherPerson, unterrichtsform, previewData]);

  const lessonCount = useMemo(() => {
    if (selectedPackage && typeof selectedPackage.units === 'number') return selectedPackage.units;
    return null;
  }, [selectedPackage]);

  const lessonCountLabel = typeof lessonCount === 'number' ? String(lessonCount) : '—';
  const honorarLabel = toMoney(honorarPerLessonCents, selectedPackage?.currency || 'EUR');
  const totalValueCents = typeof honorarPerLessonCents === 'number' && typeof lessonCount === 'number'
    ? honorarPerLessonCents * lessonCount
    : null;
  const honorarMissing = honorarPerLessonCents === null || honorarPerLessonCents <= 0;
  const honorarMissingMessage = `Angebot kann nicht gesendet werden: Bei ${previewData?.teacherName || 'Lehrer'} ist kein Honorar${unterrichtsform?.name ? ` für ${unterrichtsform.name}` : ''} hinterlegt. Bitte im Lehrerprofil unter Honorare pflegen.`;
  // Nur die Unterrichtsform — die Termin-Anzahl steht eine Zeile darunter
  // ohnehin als eigenes Feld.
  const serviceLabel = previewData?.form || 'Unterrichtsform';

  const teacherAddressLines = useMemo(() => {
    const lines: string[] = [];
    const street = String(teacherPerson?.structuredAddress?.street || '').trim();
    const zip = String(teacherPerson?.structuredAddress?.postalCode || '').trim();
    const city = String(teacherPerson?.structuredAddress?.city || '').trim();
    const zipCity = [zip, city].filter(Boolean).join(' ').trim();
    if (street) lines.push(street);
    if (zipCity) lines.push(zipCity);
    if (lines.length === 0) {
      const fallback = String(teacherPerson?.address || '').trim();
      if (fallback) {
        fallback.split('\n').map((line) => line.trim()).filter(Boolean).forEach((line) => lines.push(line));
      }
    }
    return lines;
  }, [teacherPerson]);

  const teacherSalutationLine = useMemo(() => {
    const salutation = String(teacherPerson?.salutation || '').trim();
    const teacherName = String(teacherPerson?.name || previewData?.teacherName || 'Lehrer').trim();
    if (salutation) return `${salutation} ${teacherName}`.trim();
    return `Herr/Frau ${teacherName}`.trim();
  }, [teacherPerson, previewData]);

  // CommunicationComposer-Variablen
  const templateVars = useMemo(() => {
    const studentParts = splitStudentName(previewData?.studentName);
    const snapshot = (caseItem?.snapshot ?? {}) as Record<string, unknown>;
    const legacyContractId = String(snapshot.legacy_contract_id ?? snapshot.legacy_contract_number ?? '').trim();
    return {
      studentName: previewData?.studentName || '',
      schueler_vorname: studentParts.first,
      schueler_nachname: studentParts.last,
      teacherName: previewData?.teacherName || '',
      instrument: previewData?.instrument || '',
      fach: previewData?.instrument || '',
      form: previewData?.form || '',
      unterrichtsform: previewData?.form || '',
      weekday: previewData?.weekday || '',
      wochentags: previewData?.weekday ? toWochentags(previewData.weekday) : '',
      timeFrom: previewData?.startTime || '',
      timeTo: previewData?.endTime || '',
      start: previewData?.startTime || '',
      ende: previewData?.endTime || '',
      startDate: previewData?.startDate || '',
      auftragsstart: previewData?.startDate || '',
      neues_startdatum: previewData?.startDate || '',
      roomName: previewData?.roomName || '',
      raum: previewData?.roomName || '',
      raum_adresse: offerAddress,
      date: previewData?.startDate || '',
      time: previewData?.startTime || '',
      classroom: previewData?.roomName || '',
      orderId: previewData?.orderId ?? null,
      neue_auftragsnummer: previewData?.orderId ?? '',
      alte_auftragsnummer: legacyContractId,
      honorar: honorarLabel,
      neues_honorar: honorarLabel,
      lehrer_absagen: '',
      aussetzen: '',
      auftragsende: '',
    };
  }, [caseItem?.snapshot, previewData, offerAddress, honorarLabel]);

  const composerGroups: RecipientGroup[] = useMemo(() => {
    if (teacherWpUserId <= 0 || !previewData) return [];
    return [
      {
        key: 'teacher',
        label: 'Lehrer',
        recipients: [
          {
            id: `person:${teacherWpUserId}:`,
            type: 'person',
            wpUserId: teacherWpUserId,
            subIndex: null,
            name: previewData.teacherName,
          },
        ],
        templateKeys: { email: 'order.offer' },
        vars: templateVars,
        defaultSelectedChannels: { email: true, sms: false },
      },
    ];
  }, [teacherWpUserId, previewData, templateVars]);

  // Reset beim Öffnen
  useEffect(() => {
    if (!open) return;
    setStep(1);
    setSendEmailTeacher(true);
    setSendSmsTeacher(false);
    setEmailSubjectTeacher('');
    setEmailHtmlTeacher('');
    setEmailTextTeacher('');
    setHistoryHtml('');
    setTeacherPerson(null);
    setUnterrichtsform(null);
    setSelectedPackage(null);
    setOfferAddress('—');
  }, [open]);

  // Preview-Daten laden (Lehrer, Unterrichtsform, Paket, Raum, Standort)
  useEffect(() => {
    if (!open || !previewData) return;
    let cancelled = false;
    const load = async () => {
      setIsLoadingPreview(true);
      try {
        // Case-Details ggf. nachladen (für Refresh, derzeit ungenutzt)
        if (caseItem?.id) {
          await migrationApi.getCaseDetails(caseItem.id).catch(() => null);
        }

        // Lehrer-Person (für Salutation, Adresse, artRates)
        if (previewData.teacherWpUserId > 0) {
          try {
            const person = await api.get<TeacherPersonPreview>(`/api/personen/${previewData.teacherWpUserId}`);
            if (!cancelled) setTeacherPerson(person || null);
          } catch {
            if (!cancelled) setTeacherPerson(null);
          }
        }

        // Unterrichtsform (für art_id zur Honorar-Berechnung)
        if (previewData.unterrichtsformId > 0) {
          try {
            const forms: UnterrichtsformPreview[] = await masterdataApi.unterrichtsformen();
            if (!cancelled) {
              const found = (forms || []).find((f) => Number(f.id) === previewData.unterrichtsformId);
              setUnterrichtsform(found || { id: previewData.unterrichtsformId, name: previewData.form });
            }
          } catch {
            if (!cancelled) setUnterrichtsform({ id: previewData.unterrichtsformId, name: previewData.form });
          }
        }

        // Paket (für Anzahl Termine + Currency) — Decision-Payload hat ggf. followupPackageId.
        // Wir laden alle Pakete der Unterrichtsform und suchen die ID darin – das
        // ist robuster als preview-packages, das ggf. nur Special-Pakete liefert.
        const pkgId = previewData.followupPackageId || previewData.packageId;
        if (pkgId && previewData.unterrichtsformId > 0) {
          try {
            const allPackages = await api.get<PackagePreview[]>(
              `/api/unterrichtsformen/${encodeURIComponent(String(previewData.unterrichtsformId))}/packages`
            );
            if (!cancelled) {
              const found = (allPackages || []).find((p) => String(p.id) === pkgId);
              setSelectedPackage(found || null);
            }
          } catch {
            if (!cancelled) setSelectedPackage(null);
          }
        }

        // Raum-Adresse + Standort-Name → Adresse-Block
        let standortName = '';
        let room: Room | null = null;
        try {
          if (standortId > 0) {
            const standortData = await api.get<{ name?: string }>(`/api/standorte/${standortId}`);
            standortName = String(standortData?.name || '').trim();
          }
        } catch {
          standortName = '';
        }
        try {
          if (previewData.roomId > 0) {
            room = await getRoom(previewData.roomId);
          }
        } catch {
          room = null;
        }
        if (!cancelled) {
          const roomAddress = room
            ? [
                room.address_street ? `${room.address_street}${room.address_house_number ? ` ${room.address_house_number}` : ''}` : '',
                [room.address_postal_code, room.address_city].filter(Boolean).join(' '),
              ].filter((line) => String(line || '').trim() !== '').join(', ')
            : '';
          const roomNameText = String(room?.name || previewData.roomName || '').trim();
          const addressLines = [
            standortName ? `FIT IN music ${standortName}` : 'FIT IN music',
            roomAddress,
            roomNameText,
          ].filter((line) => line && line.trim() !== '');
          setOfferAddress(addressLines.length > 0 ? addressLines.join('\n') : '—');
        }
      } finally {
        if (!cancelled) setIsLoadingPreview(false);
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, [open, caseItem?.id, previewData, standortId]);

  // Historie-Template rendern
  useEffect(() => {
    if (!open || !previewData) return;
    let cancelled = false;
    const loadTemplatePreview = async () => {
      try {
        const rendered: RenderHistoryTemplateResponse = await historyTemplatesApi.render({
          template_key: HISTORY_TEMPLATE_KEY,
          standort_id: standortId || undefined,
          vars: templateVars,
        });
        if (cancelled) return;
        setHistoryHtml(rendered.history || '');
      } catch {
        if (!cancelled) {
          setHistoryHtml(HISTORY_DEFAULT(
            previewData.teacherName,
            previewData.studentName,
            previewData.startDate ? formatDate(previewData.startDate) : '',
          ));
        }
      }
    };
    void loadTemplatePreview();
    return () => {
      cancelled = true;
    };
  }, [open, previewData, templateVars, standortId]);

  const canProceedStep2 = teacherWpUserId > 0 && sendEmailTeacher;
  const canSubmit = sendEmailTeacher
    && emailSubjectTeacher.trim().length > 0
    && emailHtmlTeacher.trim().length > 0;

  const handleSubmit = async () => {
    if (!caseItem) return;
    if (honorarPerLessonCents === null || lessonCount === null || totalValueCents === null) {
      toast.error(honorarMissingMessage);
      return;
    }
    setIsSubmitting(true);
    try {
      const res = await migrationApi.sendCaseOffer(caseItem.id, {
        // Der Text ist eine Vorlage mit Platzhaltern; ohne die Werte blieben sie in der
        // Mail leer. Den Annahme-Link ({{orderLink}}) ergänzt der Server.
        email: {
          subject: emailSubjectTeacher,
          html: emailHtmlTeacher,
          text: emailTextTeacher || undefined,
          vars: templateVars,
        },
        history: historyHtml ? { description: historyHtml } : undefined,
        offer: {
          honorarCents: honorarPerLessonCents,
          lessonCount,
          totalValueCents,
          currency: selectedPackage?.currency || 'EUR',
        },
      });
      if (res.ok && res.offer_sent) {
        toast.success('Angebot an Lehrer versendet');
      } else if (res.errors && res.errors.length > 0) {
        toast.error('Versand mit Hinweisen: ' + res.errors.join(' · '));
      } else {
        toast.success('Verarbeitet');
      }
      if (onDone) await onDone();
      onOpenChange(false);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Angebot konnte nicht versendet werden');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <WizardShell
      open={open}
      onOpenChange={onOpenChange}
      title="Angebot senden"
      description={`Schritt ${step} von 3`}
      currentStep={step}
      steps={[{ label: 'Angebotsvorschau' }, { label: 'Kommunikation' }, { label: 'Übersicht' }]}
      onBack={step > 1 ? () => moveStep(-1) : undefined}
      canBack={step > 1 && !isSubmitting}
      onNext={step < 3 ? () => moveStep(1) : undefined}
      canNext={step === 1 ? !honorarMissing : step === 2 ? canProceedStep2 : false}
      onPrimary={step === 3 ? handleSubmit : undefined}
      primaryLabel={isSubmitting ? 'Sende…' : 'Absenden'}
      canPrimary={step === 3 && canSubmit && !isSubmitting}
      isLoading={isSubmitting}
    >
      <div className="space-y-4 py-4 motion-safe:animate-fade-up">
        {step === 1 ? (
          <WizardCard>
            {honorarMissing ? (
              <Alert variant="destructive" className="mb-4">
                <AlertTriangle className="h-4 w-4" />
                <AlertTitle>Honorar fehlt</AlertTitle>
                <AlertDescription>{honorarMissingMessage}</AlertDescription>
              </Alert>
            ) : null}
            <WizardSection title="Angebotsvorschau">
              <div className="rounded-md border bg-white p-8 text-black shadow-sm">
                <div className="flex items-start justify-between">
                  <div>
                    <div className="text-2xl font-bold leading-none">FIT IN music</div>
                    <div className="mt-1 text-base font-semibold">Deine Musikschule</div>
                    <div className="mt-5 text-[12px] leading-5">
                      FIT IN music GmbH &amp; Co. KG · Jasperallee 33 · 38102 Braunschweig
                    </div>
                  </div>
                  <div className="text-right text-[13px] leading-6">
                    <div>Braunschweig, den {previewData?.startDate ? formatDate(previewData.startDate) : '—'}</div>
                  </div>
                </div>

                <div className="mt-5 text-[14px] leading-6">
                  <div>{teacherSalutationLine}</div>
                  {teacherAddressLines.length > 0 ? (
                    teacherAddressLines.map((line) => (
                      <div key={line}>{line}</div>
                    ))
                  ) : (
                    <div>Adressdaten aus Lehrerprofil</div>
                  )}
                </div>

                <div className="mt-7">
                  <div className="text-[37px] font-semibold leading-tight tracking-tight">Angebot</div>
                </div>

                <div className="mt-7 border-b border-black pb-2 text-[17px] leading-8">
                  <div>Hallo {(previewData?.teacherName || 'Lehrer').split(' ')[0]},</div>
                  <div>hiermit bieten wir dir folgendes Angebot an:</div>
                </div>

                <div className="mt-4 border border-black/70">
                  {[
                    ['Schüler/in', previewData?.studentName || '—'],
                    [getSubjectLabels().singular, previewData?.instrument || '—'],
                    ['Leistung', serviceLabel],
                    ['Anzahl Termine', lessonCountLabel],
                    ['Stundensatz', toMoney(stundensatzAus(honorarPerLessonCents, previewData?.durationMin), selectedPackage?.currency || 'EUR')],
                    ['Honorar je Termin', honorarLabel],
                    ['Gesamtbetrag', toMoney(totalValueCents, selectedPackage?.currency || 'EUR')],
                    ['Startdatum', formatDateOnly(previewData?.startDate || null)],
                    ['Termin', `${previewData?.weekday ? toWochentags(previewData.weekday) + ', ' : ''}${previewData?.startTime || '--:--'} - ${previewData?.endTime || '--:--'} Uhr`],
                    ['Adresse', offerAddress],
                  ].map(([label, value]) => (
                    <div key={label} className="grid grid-cols-[220px_1fr] border-b border-black/40 text-[14px] leading-6 last:border-b-0">
                      <div className="border-r border-black/40 px-3 py-1.5 font-semibold">{label}</div>
                      <div className="px-3 py-1.5 whitespace-pre-line">{value}</div>
                    </div>
                  ))}
                </div>

                {/* ANG-1: Auftragsabschluss-Bereich aus Angebot gelöscht — gehört
                    nur in die Auftragsbestätigung (UNT-1). */}

                <div className="mt-10 border-t border-black pt-4 text-[12px] leading-5">
                  <div className="flex items-start justify-between gap-6">
                    <div>
                      <div className="font-semibold">FIT IN music GmbH &amp; Co. KG</div>
                      <div>Jasperallee 33 · 38102 Braunschweig</div>
                      <div>Tel. +49 (0) 609 55 66-0</div>
                    </div>
                    <div className="text-right">
                      <div>Amtsgericht Braunschweig · HRA 201595</div>
                      <div>persönlich haftende Gesellschafterin:</div>
                      <div>FIT IN Verwaltung GmbH · HRB 205015</div>
                    </div>
                  </div>
                </div>

                <div className="mt-3 text-xs text-muted-foreground">
                  Vorschau-Modus: Darstellung orientiert sich am späteren Auftrags-PDF.
                </div>
              </div>
            </WizardSection>
            {isLoadingPreview ? <div className="text-xs text-muted-foreground">Lade Vorschau-Daten…</div> : null}
          </WizardCard>
        ) : null}

        {step === 2 ? (
          <div className="space-y-3">
            {!teacherWpUserId ? (
              <p className="text-sm text-destructive">Kein Lehrer am Vertrag hinterlegt. Angebot kann nicht versendet werden.</p>
            ) : null}
            {teacherWpUserId > 0 && standortId > 0 ? (
              <CommunicationComposer
                active={open && step === 2}
                standortId={standortId}
                groups={composerGroups}
                allowedChannels={{ email: true, sms: false }}
                onDraftsChange={({ groups }: { groups: Record<string, GroupDraftState> }) => {
                  const teacher = groups.teacher;
                  if (!teacher) return;
                  setSendEmailTeacher(!!teacher.selectedChannels.email);
                  setSendSmsTeacher(!!teacher.selectedChannels.sms);
                  setEmailSubjectTeacher(teacher.email.subjectTpl || '');
                  setEmailHtmlTeacher(teacher.email.htmlTpl || '');
                  setEmailTextTeacher(teacher.email.textTpl || '');
                }}
              />
            ) : null}
          </div>
        ) : null}

        {step === 3 ? (
          <WizardCard>
            <WizardSection title="Übersicht">
              <WizardKVGrid>
                <WizardKVItem label="Lehrer">{previewData?.teacherName || '—'}</WizardKVItem>
                <WizardKVItem label="Schüler">{previewData?.studentName || '—'}</WizardKVItem>
                <WizardKVItem label={getSubjectLabels().singular}>{previewData?.instrument || '—'}</WizardKVItem>
                <WizardKVItem label="Termin">
                  {previewData?.startDate ? formatDate(previewData.startDate) : '—'} · {previewData?.startTime || '--:--'} - {previewData?.endTime || '--:--'}
                </WizardKVItem>
                <WizardKVItem label="Order-ID">{previewData?.orderId || '—'}</WizardKVItem>
              </WizardKVGrid>
            </WizardSection>

            <WizardSection title="Benachrichtigungen">
              <WizardKVGrid>
                {composerGroups.map((group) => (
                  <WizardKVItem key={group.key} label={group.label}>
                    <div className="flex flex-wrap items-center gap-2">
                      <span>{group.recipients[0]?.name || group.label}</span>
                      <span className={channelPillClass(sendEmailTeacher)}>E-Mail</span>
                      <span className={channelPillClass(sendSmsTeacher)}>SMS</span>
                    </div>
                  </WizardKVItem>
                ))}
              </WizardKVGrid>
            </WizardSection>

            <WizardSection title="Historie">
              <div className="space-y-2">
                <Label>Historieneintrag</Label>
                <Textarea
                  value={historyHtml}
                  onChange={(e) => setHistoryHtml(e.target.value)}
                  className="min-h-[110px]"
                />
              </div>
            </WizardSection>
          </WizardCard>
        ) : null}
      </div>
    </WizardShell>
  );
};

export default MigrationOfferWizardModal;
